import * as rpc from "@1games-ru/rage-rpc";
import { CallOptions } from "@1games-ru/rage-rpc";
import {
  RpcMpProviderCallback,
  RpcMpProviderUnsubscribe,
} from "@/core/rpc-mp-provider/interfaces/rpc-mp-provider-types";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response.ts";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response.ts";
import { IS_DEV } from "@/domains/main/constants/other";

export default class RpcMpProvider {
  public static instance: RpcMpProvider;

  public subscribe(eventName: string, callback: RpcMpProviderCallback): RpcMpProviderUnsubscribe {
    rpc.register(eventName, callback);

    return () => this.unsubscribe(eventName);
  }

  // eslint-disable-next-line class-methods-use-this
  public unsubscribe(eventName: string): void {
    rpc.unregister(eventName);
  }

  public async callServer<T, K>(
    eventName: string,
    data: T,
    options?: CallOptions,
  ): Promise<RpcMpProviderSuccessResponse<K> | RpcMpProviderFailedResponse> {
    if (IS_DEV) {
      return new Promise((resolve) => {
        resolve(new RpcMpProviderSuccessResponse());
      });
    }
    return rpc
      .callServer(eventName, data, options || undefined)
      .then((result: K) => this.transform<K>(result as object))
      .catch((err: Error) => this.transformToFailed(err));
  }

  public static async callServer<T, K>(
    eventName: string,
    data: T,
    options?: CallOptions,
  ): Promise<RpcMpProviderSuccessResponse<K> | RpcMpProviderFailedResponse> {
    return RpcMpProvider.getInstance().callServer<T, K>(eventName, data, options);
  }

  public async callClient<T, K>(
    eventName: string,
    data: T,
    options?: CallOptions,
  ): Promise<RpcMpProviderSuccessResponse<K> | RpcMpProviderFailedResponse> {
    if (IS_DEV) {
      return new Promise((resolve) => {
        resolve(new RpcMpProviderSuccessResponse());
      });
    }
    return rpc
      .callClient(eventName, data, options || undefined)
      .then((result: K) => this.transform<K>(result as object))
      .catch((err: Error) => this.transformToFailed(err));
  }

  public static async callClient<T, K>(
    eventName: string,
    data: T,
    options?: CallOptions,
  ): Promise<RpcMpProviderSuccessResponse<K> | RpcMpProviderFailedResponse> {
    return RpcMpProvider.getInstance().callClient<T, K>(eventName, data, options);
  }

  public transform<T>(
    result: unknown,
  ): RpcMpProviderSuccessResponse<T> | RpcMpProviderFailedResponse {
    if (typeof result === "object" && "status" in result && result.status && "data" in result) {
      return this.transformToSuccess<T>(result.data as T);
    }

    if (
      typeof result === "object" &&
      "status" in result &&
      !result.status &&
      "message" in result &&
      typeof result.message === "string"
    ) {
      return this.transformToFailed(new Error(result.message));
    }

    return this.transformToSuccess<T>(result as T);
  }

  // eslint-disable-next-line class-methods-use-this
  public transformToSuccess<T>(result: T): RpcMpProviderSuccessResponse<T> {
    const response = new RpcMpProviderSuccessResponse<T>();
    response.status = true;
    response.data = result as T;

    return response;
  }

  // eslint-disable-next-line class-methods-use-this
  public transformToFailed(error: Error): RpcMpProviderFailedResponse {
    return new RpcMpProviderFailedResponse(error);
  }

  public static getInstance(): RpcMpProvider {
    if (!RpcMpProvider.instance) {
      RpcMpProvider.instance = new RpcMpProvider();
    }

    return RpcMpProvider.instance;
  }
}
